import { useCallback, useEffect, useRef, useState } from "react";
import type { EntityItemConfig, EntitySelection } from "@salestrak/ui";
import type { EntityRefValue, EntityType } from "#app/lib/data";
import type { useIssueViewMutations } from "#app/hooks/issue-view/use-issue-view-mutations";
import { useMentionAccessActions } from "#app/lib/mention-access-dialog-context";
import { isSuspended } from "#app/lib/role-utils";
import { USER_POOL_ID } from "#app/components/entity-selectors/hooks/use-user-pool";
import { assigneeRefToEntityItem } from "#app/components/entity-selectors/utils";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

// Assignees live in the shared user pool.
const ASSIGNEE_POOL_ID = USER_POOL_ID;

// Builds the selector selection for one assignee ref. Suspended assignees are
// filtered out of the active-only pool, so rebuild them from the ref (the
// muted flag flows into the avatar render). First-position assignee is primary.
export function assigneeToSelection(
  assignee: EntityRefValue,
  poolMap: Map<string, EntityItemConfig>,
): EntitySelection {
  const suspended = isSuspended(assignee.user);
  const poolItem = suspended ? undefined : poolMap.get(assignee.entityId);
  const base = poolItem ?? assigneeRefToEntityItem(assignee);
  return {
    id: assignee.entityId,
    poolId: ASSIGNEE_POOL_ID,
    entity: {
      ...base,
      description: assignee.isPrimary ? "Primary" : undefined,
    },
  };
}

type UpdateAssigneesFn = ReturnType<typeof useIssueViewMutations>["updateAssignees"];

export type UseAssigneeSelectionParams = Omittable<{
  /** Selections derived from the current assignees (or the create-mode draft). */
  initialSelections: EntitySelection[];
  /**
   * Issue to mutate — `id` is the GraphQL id, `issueId` the public id. Pass
   * `null` in create mode (the issue doesn't exist yet).
   */
  issue: { id: string; issueId: string } | null;
  /** Update-assignees mutation, already gated to a no-op for read-only viewers. */
  updateAssignees: UpdateAssigneesFn;
  /** Viewer access — drives the grant-access prompt's share actions. */
  viewerCanManageSharing: boolean;
  /**
   * Create mode: when set, value/primary changes write here instead of firing
   * the debounced server mutation, and the close-prompt is skipped (no issue
   * exists to grant access to yet).
   */
  onDraftChange?: (selections: EntitySelection[]) => void;
}>;

export interface AssigneeSelectionController {
  draftSelections: EntitySelection[];
  handleValueChange: (next: EntitySelection[]) => void;
  handleOpenChange: (nextOpen: boolean) => void;
  getSetPrimaryLabel: (selection: EntitySelection) => string | undefined;
  handleSetPrimary: (entity: EntityItemConfig) => void;
}

// Encapsulates the assignee selector's draft state, debounced auto-save, the
// changeable-primary reordering, and the grant-access prompt fired once per
// selector session on close. Shared by the issue-view properties panel and the
// issue-card compact selector — the only variation is create mode (`onDraftChange`).
export function useAssigneeSelection({
  initialSelections,
  issue,
  updateAssignees,
  viewerCanManageSharing,
  onDraftChange,
}: UseAssigneeSelectionParams): AssigneeSelectionController {
  const { promptIfMissing } = useMentionAccessActions();

  const [draftSelections, setDraftSelections] =
    useState<EntitySelection[]>(initialSelections);
  const draftRef = useRef(initialSelections);
  const isOpenRef = useRef(false);
  // Assignee ids present when the selector opened — diffed on close so the
  // missing-access prompt fires once per session, after the dropdown closes,
  // instead of mid-selection on every toggle.
  const idsAtOpenRef = useRef<Set<string>>(new Set());

  // Sync the draft when external assignees change while the popover is closed.
  useEffect(() => {
    if (!isOpenRef.current) {
      draftRef.current = initialSelections;
      setDraftSelections(initialSelections);
    }
  }, [initialSelections]);

  const mutateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(
    () => () => {
      if (mutateTimeoutRef.current != null) clearTimeout(mutateTimeoutRef.current);
    },
    [],
  );

  const flushMutation = useCallback(
    (selections: EntitySelection[]) => {
      if (!issue) return;
      // The missing-access prompt fires from handleOpenChange when the selector
      // closes — not here — so the dialog never pops mid-selection.
      const assigneesInput = selections.map((s, index) => ({
        entityType: ((s.entity.data?.entityType as string) ?? "user") as EntityType,
        entityId: s.id,
        isPrimary: index === 0,
      }));
      updateAssignees({
        variables: { issueId: issue.issueId, assignees: assigneesInput },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue",
            id: issue.id,
            issueId: issue.issueId,
            assignees: selections.map((s, index) => ({
              __typename: "EntityRef" as const,
              entityType: (s.entity.data?.entityType as string) ?? "user",
              entityId: s.id,
              isPrimary: index === 0,
              user: {
                __typename: "User" as const,
                id: s.id,
                userId: s.id,
                name: s.entity.title,
                email: s.entity.description ?? "",
                username: s.entity.description ?? null,
                profileImage: s.entity.avatar?.src ?? null,
                // IssueViewEntityRef selects memberStatus — omitting it triggers
                // Apollo's "Missing field 'memberStatus'" warning on merge.
                // 'active' matches what selectable members are.
                memberStatus:
                  (s.entity.data?.memberStatus as string | null | undefined) ?? "active",
              },
              team: null,
            })),
          },
        } as any,
      });
    },
    [issue, updateAssignees],
  );

  const commit = useCallback(
    (selections: EntitySelection[]) => {
      if (onDraftChange) {
        onDraftChange(selections);
        return;
      }
      if (mutateTimeoutRef.current != null) clearTimeout(mutateTimeoutRef.current);
      mutateTimeoutRef.current = setTimeout(() => flushMutation(selections), 300);
    },
    [onDraftChange, flushMutation],
  );

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      draftRef.current = next;
      setDraftSelections(next);
      commit(next);
    },
    [commit],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      isOpenRef.current = nextOpen;
      if (nextOpen) {
        draftRef.current = initialSelections;
        setDraftSelections(initialSelections);
        idsAtOpenRef.current = new Set(initialSelections.map((s) => s.id));
        return;
      }
      // Selector closed — prompt for assignees added this session who can't see
      // the issue. The assignment already applied (debounced separately); the
      // prompt never blocks it. Create mode has no issue to grant access to.
      if (onDraftChange || !issue) return;
      const addedUserIds = draftRef.current
        .map((s) => s.id)
        .filter((id) => !idsAtOpenRef.current.has(id));
      if (addedUserIds.length) {
        promptIfMissing({
          issueId: issue.issueId,
          userIds: addedUserIds,
          viewerCanManageSharing,
          variant: "assignment",
        });
      }
    },
    [initialSelections, issue, onDraftChange, promptIfMissing, viewerCanManageSharing],
  );

  const getSetPrimaryLabel = useCallback((selection: EntitySelection) => {
    // First selection is primary — no action needed.
    const index = draftRef.current.findIndex((s) => s.id === selection.id);
    return index > 0 ? "Set primary" : undefined;
  }, []);

  const handleSetPrimary = useCallback(
    (entity: EntityItemConfig) => {
      const current = draftRef.current;
      const index = current.findIndex((s) => s.id === entity.id);
      if (index <= 0) return;
      // Move to front and re-flag the "Primary" description.
      const promoted = at(current, index, "selection");
      const next = [
        {
          ...promoted,
          entity: { ...promoted.entity, description: "Primary" },
        },
        ...current.slice(0, index).map((s) => ({
          ...s,
          entity: { ...s.entity, description: undefined },
        })),
        ...current.slice(index + 1).map((s) => ({
          ...s,
          entity: { ...s.entity, description: undefined },
        })),
      ];
      draftRef.current = next;
      setDraftSelections(next);
      commit(next);
    },
    [commit],
  );

  return {
    draftSelections,
    handleValueChange,
    handleOpenChange,
    getSetPrimaryLabel,
    handleSetPrimary,
  };
}
